// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import type {VNode} from 'snabbdom';
import isNil from 'lodash/isNil';

import {emitTrackEvent, KEY_VALUES, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {ItemsMenuSize, Section, SectionItem} from 'common/components/items-menu/types';
import {Placement, TooltipAttrs} from 'common/components/tooltip/types';
import {ButtonLayout, ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {findSelectedItems} from 'common/components/items-menu/utils';
import {IronContextRegistry} from 'common/report/context-registry';
import {LocalizationStore} from 'common/i18n/store';
import {i18next} from 'common/i18n';

import {Directions} from '../drop-menu/types';
import {TrackingEvent} from './tracking';
import {
  ChangeEvent,
  FocusItemEvent,
  MenuChangeEvent,
  SearchFilterChangeEvent,
  SelectAttrs,
  SelectDropdownState,
  SelectElProps,
  SelectIcon,
  SelectOpenMode,
  SelectParams,
} from './types';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import css from './index.module.styl?inline';
import {SelectTheme} from './constants';

const LABEL_TRANSFORM = {
  NONE: ``,
  UPPERCASE: `uppercase`,
};

const ICON_DEFAULT = {
  rightIcon: `caret-down`,
};

export class SelectDropdown extends Component<
  SelectDropdownState,
  SelectAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  SelectParams
> {
  private declare inputElement: HTMLInputElement;
  private focusPrefix = ``;
  private focusPrefixClearTimeoutId = null;
  private contextPanelVisibilityTimerId = null;
  private hoverMenuVisibilityTimerId = null;
  private _sectionsFn: () => Section[] = null;
  declare contextPanelPositionParentElement: HTMLElement;
  declare dropdownPositionParent: HTMLElement;

  static get attrsSchema(): AttrsSchema<SelectAttrs> {
    return {
      ...super.attrsSchema,
      'hide-input-focus-styling': {
        type: `boolean`,
        description: `if true, hide focus ring on mp-input. This is typically done because there is an item with focus`,
      },
      'clear-search-on-change': {
        type: `boolean`,
        description: `if true, automatically clear filter text on item selection`,
      },
      'force-visibility': {
        type: `boolean`,
        description: `Show the drop down regardless of its positioning`,
      },
      options: {
        type: `json`,
        default: [],
        description: `interface: string[]`,
      },
      'empty-message': {
        type: `string`,
        default: `This list is empty`,
      },
      open: `boolean`,
      'open-mode': {
        type: `string`,
        default: SelectOpenMode.Click,
        enum: Object.values(SelectOpenMode),
      },
      'preserve-search-on-close': {
        type: `boolean`,
        description: `if true, don't clear input on close`,
      },
      sections: {
        type: `json`,
        description:
          `interface: Section[]. ` +
          `Sections to pass to items-menu if options is not a simple list of strings. ` +
          `Can be passed via props too`,
      },
      'selected-option': {
        type: `string`,
        description: `Label of the selected option`,
      },
      label: {
        type: `string`,
        default: null,
        description: `Overrides the selected label.`,
      },
      'shortcut-label': {
        type: `string`,
        default: null,
        description: `Label showing keyboard shortcut for opening the select.`,
      },
      title: {
        type: `string`,
        default: null,
        description: `Populates the title attribute of the select. This defaults label / selected label.`,
      },
      'label-transform': {
        type: `string`,
        default: LABEL_TRANSFORM.NONE,
        enum: Object.values(LABEL_TRANSFORM),
        description: `Label transform to apply on selected label and options.`,
      },
      multiple: {
        type: `boolean`,
        description: `Allow multiple selection of items`,
      },
      'multiple-save-button': {
        type: `boolean`,
        description: `Displays save button when selecting multiple items`,
      },
      'show-select-all': {
        type: `boolean`,
        description: `Provide control for selecting all/no items at once`,
      },
      'search-placeholder': {
        type: `string`,
        default: `Search`,
        description: `Text to show in an empty search box`,
      },
      'disable-search': {
        type: `boolean`,
        description: `If a search input box should be shown for searching`,
      },
      'search-threshold': {
        type: `number`,
        description: `If disable-search=false, only show search input if number of items exceeds this threshold`,
        default: 10,
      },
      'header-title': {
        type: `string`,
        description: `header-title to show what kind of selection is being done. Useful for multilevel selection`,
      },
      'back-button': {
        type: `boolean`,
        description: `Whether to show back button. Only applicable if header is non empty string`,
      },
      'menu-width': {
        type: `number`,
        default: 0,
        description: `Width of drop menu in pixels ({width: px}). Value of "0" will inherit use the width of the button. Value of "null" (or -1 for SelectReact) will not set any width, allowing the menu to grow to its contents.`,
      },
      'menu-height': {
        type: `number`,
        default: 300,
        description: `height in px`,
      },
      'menu-alignment': {type: `string`, enum: Object.values(Directions), default: Directions.Down},
      icon: {
        type: `json`,
        default: null,
        description: `interface: SelectIcon`,
      },
      'disable-tooltip': {
        type: `boolean`,
        description: `Whether or not tooltip should be disabled`,
      },
      tooltip: {
        type: `json`,
      },
      theme: {
        type: `string`,
        default: SelectTheme.Secondary,
        enum: Object.values(SelectTheme),
        description: `Dictates styling`,
      },
      layout: {
        type: `string`,
        default: ButtonLayout.Horizontal,
        enum: Object.values(ButtonLayout),
        description: `display the button and icon in a horizontal or vertical layout`,
      },
      size: {
        type: `string`,
        default: ButtonSize.Medium,
        enum: Object.values(ButtonSize),
        description: `Dictates size, doesn't work on builder themes`,
      },
      disabled: {
        type: `boolean`,
        description: `Whether or not mp-select should be disabled`,
      },
      readonly: {
        type: `boolean`,
        description: `Whether or not mp-select is read only`,
      },
      'icon-only': {
        type: `boolean`,
        description: `Whether label is excluded from the selected value`,
      },
      'no-button-truncate': `boolean`,
      'show-icon-only-down-caret': {
        type: `boolean`,
        description: `Show the down caret even when icon-only is enabled`,
      },
      'always-show-caret': {
        type: `boolean`,
        description: `some themes opt for not displaying a down caret icon, this attribute overides that setting`,
      },
      'never-show-caret': {
        type: `boolean`,
        description: `setting this attribute to true opts out of displaying the down caret icon`,
      },
      'track-name': {
        type: `string`,
        description: `control type to include in tracking properties`,
      },
      'align-all-text': {
        type: `boolean`,
        description: `align text whether icons are present or not. use when you have icons sparsely spread through the menu`,
      },
      'show-preview-tooltip': {
        type: `boolean`,
        description: `overrides 'tooltip' attribute and adds a tooltip that previews the menu content. e.g "x, y, and more..."`,
      },
      'no-rounded-corners': {
        type: `boolean`,
        description: `control to make the button's rounded corner style`,
      },
      'prevent-close': {
        type: `boolean`,
        description: `control to prevent the inner dropdown from closing due to reasons like modals`,
      },
      // TODO replace this with the better fix to potentially move focus from mp-select to mp-items-menu?
      'disable-focus': {
        type: `boolean`,
        description: `disable the focus event listener. example usage: a rendered input in mp-select that when typed focuses items instead`,
      },
      // disable the extra padding for the first item, with or without header
      'disable-first-item-padding': `boolean`,
      'experimental-positioning': {
        type: `boolean`,
        description: `When set, makes the dropdown render along the document.body tag
          and not within the DOM location of the mp-select element, before this becomes
          default behavior, we need further testing to remove slots from mp-select
        `,
      },
      offset: {
        type: `number`,
        default: null,
      },
      'list-size': {
        type: `string`,
        default: ItemsMenuSize.Default,
        description: `Size passed to ItemsMenu (ItemsMenuSize.Small)`,
      },
      'disabled-stays-opaque': {
        type: `boolean`,
        description: `When disabled, the button stays opaque`,
      },
      'show-section-label-matches': {
        type: `boolean`,
        description: `Whether to show the entire section when the search filter matches with the label`,
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        isOpen: false,
        isSearchable: false,
        searchFilter: ``,
        sections: null, // if passed in via a prop
        sectionsFromInputFn: null,
        contextPanelTransform: null,
        dropdownBodyRenderer: null,
        tooltipData: null,
      },
      params: {
        sections: Array,
        tooltip: Object,
        disablePressedStyles: Boolean,
      },
      defaultContexts: {
        i18n: new LocalizationStore(i18next),
      },
      contexts: [`i18n` as const],
      helpers: {
        TooltipPlacement: Placement,

        handleClickItem: this.handleClickItem.bind(this),

        handleFocusItem: this.handleFocusItem.bind(this),

        handleSelectAll: (ev) => propagateEvent(this, ev),

        isDropMenuDisabled: () => {
          return this.attr(`disabled`) || this.attr(`readonly`);
        },

        hasHoverContextPanel: () => {
          return Boolean(this.state.contextPanelTransform) && !this.helpers.hasSlot(`trigger`);
        },

        isHoverOpenMode: () => {
          return this.attr(`open-mode`) === SelectOpenMode.Hover;
        },

        handleTriggerMouseLeave: () => {
          if (this.helpers.hasHoverContextPanel()) {
            this.delayHideContextPanel();
          }
          if (this.helpers.isHoverOpenMode() && !this.helpers.isDropMenuDisabled()) {
            this.delayHideHoverMenu();
          }
        },

        handleTriggerMouseEnter: () => {
          if (this.helpers.hasHoverContextPanel()) {
            this.delayShowContextPanel();
          }
          if (this.helpers.isHoverOpenMode() && !this.helpers.isDropMenuDisabled()) {
            this.openHoverMenu();
          }
        },

        handleMenuMouseEnter: () => {
          if (this.helpers.isHoverOpenMode()) {
            this.clearHoverMenuVisibilityTimer();
          }
        },

        handleMenuMouseLeave: () => {
          if (this.helpers.isHoverOpenMode()) {
            this.delayHideHoverMenu();
          }
        },

        handleContextPanelHoverIn: () => this.clearContextPanelVisibilityTimer(),
        handleContextPanelHoverOut: () => this.delayHideContextPanel(),
        itemContextPanelTransform: () => {
          if (this.state?.contextPanelTransform !== null) {
            return (item: any) => this.state?.contextPanelTransform(item, false);
          }
          return null;
        },

        isButtonSelect: () => {
          const theme = this.attr(`theme`);
          const hasTriggerSlot = this.helpers.hasSlot(`trigger`);

          return !(hasTriggerSlot || theme === SelectTheme.Builder || theme === SelectTheme.BuilderLight);
        },

        getItemsMenuSections: this.getItemsMenuSections.bind(this),

        getIcon: (): SelectIcon => {
          // only some themes have right caret-down icon by default
          const defaultIcon =
            this.helpers.isButtonSelect() && this.attr(`theme`) !== SelectTheme.MenuFooter ? ICON_DEFAULT : {};
          return Object.assign({}, defaultIcon, this.attr(`icon`));
        },

        getTriggerButtonTheme: (): ButtonTheme => {
          const theme = this.attr(`theme`);
          if (this.attr(`readonly`) || theme === SelectTheme.MenuFilter) {
            return ButtonTheme.Tertiary;
          }
          return this.getButtonTheme(theme);
        },

        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        selectedLabel: (): string => {
          if (this.attr(`label`)) {
            return this.attr(`label`);
          }

          const selectedOption = this.attr(`selected-option`);
          if (selectedOption) {
            return selectedOption;
          }

          // if selected-option is not supplied
          // fall back to the isSelected item's label from sections
          return this.getSelectedMenuItemLabel();
        },

        getTitle: (): string => {
          return this.attr(`title`) ?? this.helpers.selectedLabel();
        },

        hasSelectedLabel: () => !this.attr(`icon-only`) && Boolean(this.helpers.selectedLabel()),

        getDropMenuHeightStyle: () => {
          const menuHeight = this.attr(`menu-height`);
          if (!menuHeight) {
            return {'max-height': `none`};
          }

          return {'max-height': `${menuHeight}px`};
        },

        getDropMenuWidthStyle: () => {
          let menuWidth = this.attr(`menu-width`);
          if (isNil(menuWidth) || menuWidth === -1) {
            return {};
          }
          if (menuWidth === 0) {
            menuWidth = this.getDropdownPositionParent().clientWidth;
          }
          if (this.isSearchable()) {
            // if isSearchable, then have a minimum width so search input is still visible
            const width = typeof menuWidth === `number` ? menuWidth : Number.parseInt(menuWidth as string, 10);
            menuWidth = Math.max(width, 135);
          }

          return {width: `${menuWidth}px`};
        },

        getDropMenuPaddingStyle: () => {
          if (this.attr(`list-size`) === ItemsMenuSize.Small) {
            return {'padding-left': `10px`, 'padding-right': `10px`};
          }
          return {};
        },

        isSearchable: () => this.isSearchable(),

        shouldShowRightIcon: () => {
          const theme = this.attr(`theme`);
          const isNoCaretTheme = [SelectTheme.Tertiary, SelectTheme.Select, SelectTheme.QbSubitem].includes(theme);
          return isNoCaretTheme ? this.attr(`always-show-caret`) : !this.attr(`never-show-caret`);
        },

        handleSearchInputInsert: (vnode) => (this.inputElement = vnode.elm),

        handleSearchInput: (value) => {
          this.update({searchFilter: value});
          this.dispatchEvent(
            new CustomEvent(`searchFilterChange`, {
              detail: {searchFilter: value},
            }) as SearchFilterChangeEvent,
          );
        },

        handleDropMenuChange: ({detail}) => {
          if (detail.state === `closed` && this.state.isOpen === true) {
            this.setDropMenuIsOpen(false);
          } else if (this.inputElement) {
            requestAnimationFrame(() => this.inputElement.focus());
          }
        },

        handleSaveClicked: () => {
          this.dispatchEvent(new CustomEvent(`saveClicked`));
          this.setDropMenuIsOpen(false);
        },

        handleDropMenuInsert: (vnode) => {
          this.dispatchEvent(new CustomEvent(`menuInsert`, {detail: {vnode}}));
        },

        handleBackClick: () => {
          this.update({searchFilter: ``});
          this.dispatchEvent(new CustomEvent(`backClick`));
        },

        getTooltip: (): TooltipAttrs => {
          if (this.attr(`disable-tooltip`)) {
            return null;
          }

          if (this.attr(`show-preview-tooltip`)) {
            return this.getPreviewTooltip();
          }

          const tooltipAttr = this.params.tooltip || this.attr(`tooltip`);
          if (tooltipAttr) {
            if (!tooltipAttr.text) {
              // if only the placement is specified get the selected item's text
              return this.getSelectedTooltip(tooltipAttr);
            }
            return tooltipAttr;
          }

          if (!this.attr(`multiple`)) {
            // MenuFilter theme is a custom theme for event menu & properties menu filter dropdown
            // Adding this conditional so we do not change other places.
            if (this.attr(`theme`) === SelectTheme.MenuFilter) {
              return this.getSelectedTooltip({placement: Placement.RightCenter});
            }
            return this.getSelectedTooltip({placement: Placement.Top});
          }

          return null;
        },
      },
    };
  }

  getDropdownPositionParent() {
    return this.dropdownPositionParent ?? this;
  }

  /** @param val - pass sections via prop for perf reasons */
  set sections(val: Section[]) {
    this.update({sections: val});
  }

  /** @param value - a function that lazy computes sections to be shown, only when menu is opened */
  set sectionsFn(value: () => Section[]) {
    this._sectionsFn = value;
    this.updateSectionsFromSectionsFn();
  }

  /** @param value - function that returns a new section to be added depending on user input */
  set sectionsFromInputFn(value: (input: string) => Section[]) {
    this.update({sectionsFromInputFn: value});
  }

  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(value: (item: any, isExpanded?: boolean) => ContextPanelData) {
    this.update({contextPanelTransform: value});
  }

  set dropdownBodyRenderer(value: () => VNode) {
    this.update({dropdownBodyRenderer: value});
  }

  connectedCallback() {
    super.connectedCallback();
    this.setFocusable(!(this.attr(`disabled`) || this.attr(`readonly`)));
    const hasTriggerSlot = this.helpers.hasSlot(`trigger`);
    if (hasTriggerSlot) {
      this.classList.add(`has-trigger-slot`);
      if (!this.attr(`disable-focus`)) {
        this.addEventListener(`focus`, this.handleFocus);
        this.addEventListener(`blur`, this.handleBlur);
      }
    }
    window.addEventListener(`resize`, this.handleWindowResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(`focus`, this.handleFocus);
    this.removeEventListener(`blur`, this.handleBlur);
    window.removeEventListener(`resize`, this.handleWindowResize);
    this._sectionsFn = null;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `disabled`) {
      this.setFocusable(!this.attr(attr));
    } else if (attr === `readonly`) {
      this.setFocusable(!this.attr(attr));
    } else if (attr === `open`) {
      this.setDropMenuIsOpen(this.attr(`open`));
    } else if (attr === `header-title`) {
      // clear searchFilter when header-title changes because header-title implies a different menu
      this.update({searchFilter: ``});
      this.updateSectionsFromSectionsFn();
    } else if (attr === `menu-width`) {
      // no need to recalculate menu width if there is already one assigned
      window.removeEventListener(`resize`, this.handleWindowResize);
    } else if (attr === `disable-focus`) {
      this.removeEventListener(`focus`, this.handleFocus);
    }
  }

  handleFocus = () => {
    this.addEventListener(`keydown`, this.handleKeydown);
  };

  handleBlur = (ev) => {
    this.removeEventListener(`keydown`, this.handleKeydown);
    const path = ev.path || (ev.composedPath && ev.composedPath());
    if (path && !path.includes(this) && !path.includes(this.shadowRoot)) {
      // don't close the dropdown if the click came from inside mp-select e.g from a footer slot
      this.setDropMenuIsOpen(false);
    }
  };

  isSearchable() {
    return (
      this.state.isSearchable ||
      (!this.attr(`disable-search`) &&
        (!this.attr(`search-threshold`) ||
          this.attr(`search-threshold`) <=
            this.getItemsMenuSections().reduce((acc, section) => acc + section.items.length, 0)))
    );
  }

  handleKeydown(ev) {
    if (this.attr(`disabled`) || this.attr(`readonly`)) {
      return;
    }

    const {ARROW_UP, ARROW_DOWN, ARROW_LEFT, ENTER, BACKSPACE} = KEY_VALUES;
    const {key} = ev;
    const isOpen = this.state.isOpen;
    const searchFilter = this.state.searchFilter;

    if (isOpen) {
      if (key === ARROW_LEFT && this.attr(`back-button`) && !searchFilter) {
        // same as doing a back button click
        this.helpers.handleBackClick();
      } else if (key === BACKSPACE) {
        this.focusPrefix = this.focusPrefix.slice(0, -1);
      } else if (!this.isSearchable() && key.match(/^\w$/g)) {
        this.focusPrefix += key;
        this.focusItemWithPrefix();
      }
    } else {
      // arrow keys and enter open the dropdown (just like the html select)
      if (key === ENTER || key === ARROW_UP || key === ARROW_DOWN) {
        ev.stopPropagation();
        ev.preventDefault();
        this.setDropMenuIsOpen(true);
      }
    }
  }

  handleClickItem(ev) {
    const {item, parentItems, modifierKeys, shouldClose} = ev.detail;
    const changeEvent: ChangeEvent = new CustomEvent(`change`, {
      detail: {
        value: item.label,
        item,
        ...(parentItems && {parentItems}),
        modifierKeys,
      },
    });
    this.dispatchEvent(changeEvent);

    if (this.attr(`clear-search-on-change`)) {
      this.update({searchFilter: ``});
    }

    if (shouldClose && !this.attr(`multiple`) && !item.hasCaret) {
      this.setDropMenuIsOpen(false);
    }
  }

  /** Called when an item is hovered */
  handleFocusItem(ev) {
    const item = ev.detail.item;

    this.dispatchEvent(
      new CustomEvent(`focusItem`, {
        detail: {
          value: item.label,
          item,
        },
      }) as FocusItemEvent,
    );
  }

  private handleWindowResize = () => {
    // trigger a rerender to allow menu width to be recalculated when resizing
    if (this.state.isOpen) {
      this.update();
    }
  };

  getButtonTheme(selectTheme: SelectTheme): ButtonTheme {
    switch (selectTheme) {
      case SelectTheme.Primary:
        return ButtonTheme.Primary;
      case SelectTheme.Secondary:
        return ButtonTheme.Secondary;
      case SelectTheme.Tertiary:
        return ButtonTheme.Tertiary;
      case SelectTheme.Select:
        return ButtonTheme.Select;
      case SelectTheme.MenuFilter:
        return ButtonTheme.MenuFilter;
      case SelectTheme.MenuFooter:
        return ButtonTheme.MenuFooter;
      case SelectTheme.QbSubitem:
        return ButtonTheme.QbSubitem;
      case SelectTheme.QueryBuilder:
        return ButtonTheme.QueryBuilder;
      case SelectTheme.WarningActions:
        return ButtonTheme.Primary;
      default:
        return ButtonTheme.Tertiary;
    }
  }

  getItemsMenuSections(): Section[] {
    if (this.params.sections) {
      return this.params.sections;
    } else if (this.attr(`sections`)) {
      return this.attr(`sections`);
    } else if (this.state.sections) {
      return this.state.sections;
    }

    // otherwise create sections format from options and selected option
    const options = this.attr(`options`);
    const selectedLabel = this.attr(`selected-option`);
    return [
      {
        items: options.map((option) => ({
          label: option,
          isSelected: option === selectedLabel,
        })),
      },
    ];
  }

  getSelectedMenuItems(): SectionItem[] {
    const selectedLabel = this.attr(`selected-option`).toLowerCase();
    const predicate = (item: SectionItem) =>
      item.isSelected ||
      (typeof item.label === `string` &&
        !Object.prototype.hasOwnProperty.call(item, `isSelected`) &&
        item.label.toLowerCase() === selectedLabel);

    return findSelectedItems(this.getItemsMenuSections(), predicate);
  }

  getSelectedMenuItemLabel(): string {
    const selectedItems = this.getSelectedMenuItems();
    return selectedItems.length ? this.getContext(`i18n`).print(selectedItems[0].label) : ``;
  }

  updateSectionsFromSectionsFn() {
    if (typeof this._sectionsFn === `function`) {
      this.update({sections: this.state.isOpen ? this._sectionsFn() : null});
    }
  }

  setDropMenuIsOpen(isOpen) {
    if (this.state.isOpen !== isOpen) {
      this.update({
        isOpen,
        searchFilter: this.attr(`preserve-search-on-close`) ? this.state.searchFilter : ``,
        isSearchable: false,
        tooltipData: null,
      });
      this.updateSectionsFromSectionsFn();
      this.dispatchEvent(new CustomEvent(`menuChange`, {detail: {isOpen}}) as MenuChangeEvent);

      if (isOpen && this.attr(`selected-option`) && !this.helpers.hasSlot(`body`)) {
        // Wait for the dropdown to render before we try to focus the item
        window.requestAnimationFrame(() => this.focusItemWithLabel(this.attr(`selected-option`)));
      }

      isOpen ? this.classList.add(`is-open`) : this.classList.remove(`is-open`);
    }
  }

  focusItemWithPrefix() {
    const itemsMenuEl: any = this.el.querySelector(`mp-items-menu`);
    itemsMenuEl.focusItemWithPrefix(this.focusPrefix);

    // clear focusPrefix after one second of inactive typing
    if (this.focusPrefixClearTimeoutId) {
      clearTimeout(this.focusPrefixClearTimeoutId);
    }

    this.focusPrefixClearTimeoutId = window.setTimeout(() => {
      this.focusPrefix = ``;
      this.focusPrefixClearTimeoutId = null;
    }, 1000);
  }

  focusItemWithLabel(label: string) {
    if (this.state.isOpen) {
      const itemsMenuEl: any = this.el.querySelector(`mp-items-menu`);
      itemsMenuEl?.focusItemWithLabel(label);
    }
  }

  setFocusable(isFocusable: boolean) {
    const hasTriggerSlot = this.helpers.hasSlot(`trigger`);
    // add tabindex=0 as a host attribute so it can be focusable via tab and shift+tab
    // just the way a normal html select is focusable by default
    if (hasTriggerSlot && isFocusable) {
      this.setAttribute(`tabindex`, `0`);
    } else {
      this.removeAttribute(`tabindex`);
    }
  }

  clearContextPanelVisibilityTimer() {
    if (this.contextPanelVisibilityTimerId) {
      clearTimeout(this.contextPanelVisibilityTimerId);
    }
    this.contextPanelVisibilityTimerId = null;
  }

  delayShowContextPanel() {
    this.clearContextPanelVisibilityTimer();
    this.contextPanelVisibilityTimerId = window.setTimeout(() => {
      const isOpen = this.state.isOpen;
      const item = this.getSelectedMenuItems()[0];
      const tooltipData = this.state?.contextPanelTransform(item, true);
      if (!isOpen) {
        this.update({tooltipData});

        if (tooltipData) {
          emitTrackEvent(this, {
            eventName: TrackingEvent.ContextPanelHover,
            props: {
              Type: this.attr(`track-name`),
              'Entity name': tooltipData.header,
              'Entity description': tooltipData.description,
            },
          });
        }
      }
    }, 375);
  }

  delayHideContextPanel() {
    this.clearContextPanelVisibilityTimer();
    this.contextPanelVisibilityTimerId = window.setTimeout(() => {
      if (!this.state.isOpen) {
        this.update({tooltipData: null});
      }
    }, 100);
  }

  clearHoverMenuVisibilityTimer() {
    if (this.hoverMenuVisibilityTimerId) {
      clearTimeout(this.hoverMenuVisibilityTimerId);
    }
    this.hoverMenuVisibilityTimerId = null;
  }

  openHoverMenu() {
    this.setDropMenuIsOpen(true);
    this.clearHoverMenuVisibilityTimer();
  }

  delayHideHoverMenu() {
    this.clearHoverMenuVisibilityTimer();
    this.hoverMenuVisibilityTimerId = window.setTimeout(() => {
      this.setDropMenuIsOpen(false);
    }, 100);
  }

  getPreviewTooltip() {
    const {t, formatList} = this.getContext(`i18n`);

    const MAX = 2;

    let labels = this.getItemsMenuSections()
      .reduce((a, b) => [...a, ...b.items], [])
      .map((item) => item.shortLabel || item.label)
      .filter(Boolean);

    if (!labels.length) {
      return null;
    }

    if (labels.length > MAX) {
      labels = labels.slice(0, MAX);
      labels.push(t(`common.overflow-more`));
    }

    return {
      text: formatList(labels),
    };
  }

  getSelectedTooltip(override: TooltipAttrs) {
    const item = this.getSelectedMenuItems()[0];
    return item?.tooltip
      ? {
          ...item.tooltip,
          ...override,
        }
      : null;
  }
}

export const SelectEl = defineAndMakeJsxFactory<SelectElProps>(`mp-select`, SelectDropdown);
export const SelectReact = panel2react<SelectElProps>(`mp-select`, SelectDropdown, {forceProperty: [`sections`]});
